import {
  type AllowanceErrorContent,
  allowanceErrorContentValues,
  type TokenUsage,
} from '@toggle/toggle';
import { formatInTimeZone } from 'date-fns-tz';

import type {
  TokenUsageStatus,
  TokenUsageUnavailableReason,
} from './useTokenUsage';

export type TokenUsageAvailability = 'available' | 'exhausted';
type TokenUsageBannerVariant = 'usage' | 'error';

const TOKEN_USAGE_CONTROL_THRESHOLD = 75;
const TOKEN_USAGE_WARNING_THRESHOLD = 90;

export interface TokenUsageUiState {
  bannerVariant?: TokenUsageBannerVariant;
  isChatDisabled: boolean;
  percentage?: number;
  popoverResetDate?: string;
  resetDate?: string;
  showControl: boolean;
}

interface GetTokenUsageUiStateParams {
  isFeatureEnabled: boolean;
  status: TokenUsageStatus;
  unavailableReason?: TokenUsageUnavailableReason;
  usage?: TokenUsage;
}

export const isTokenAllowanceError = (
  content: string
): content is AllowanceErrorContent =>
  allowanceErrorContentValues.includes(content as AllowanceErrorContent);

export const getTokenUsageAvailability = (
  usage: TokenUsage
): TokenUsageAvailability =>
  usage.limited || usage.tokens_used >= usage.tokens_limit
    ? 'exhausted'
    : 'available';

export const getTokenUsagePercentage = (usage: TokenUsage): number => {
  if (usage.tokens_limit === 0) {
    return 100;
  }

  return Math.min(
    Math.floor((usage.tokens_used * 100) / usage.tokens_limit),
    100
  );
};

const formatResetDate = (
  resetAt: string | null | undefined,
  format: string
): string | undefined => {
  if (!resetAt || !Number.isFinite(Date.parse(resetAt))) {
    return undefined;
  }

  return formatInTimeZone(resetAt, 'UTC', format);
};

export const formatTokenUsageResetDate = (
  resetAt: string | null | undefined
): string | undefined => formatResetDate(resetAt, "dd MMM yyyy, hh:mm a 'UTC'");

const formatTokenUsagePopoverResetDate = (
  resetAt: string | null | undefined
): string | undefined => formatResetDate(resetAt, "dd MMM yyyy, hh:mm a 'UTC'");

export const getTokenUsageUiState = ({
  isFeatureEnabled,
  status,
  unavailableReason,
  usage,
}: GetTokenUsageUiStateParams): TokenUsageUiState => {
  if (!isFeatureEnabled || status === 'disabled') {
    return {
      isChatDisabled: false,
      showControl: false,
    };
  }

  const isExhausted = status === 'exhausted';
  const hasUnavailableState =
    status === 'unavailable' || unavailableReason !== undefined;
  const percentage = isExhausted
    ? 100
    : usage
      ? getTokenUsagePercentage(usage)
      : undefined;
  const bannerVariant = hasUnavailableState
    ? 'error'
    : percentage !== undefined && percentage >= TOKEN_USAGE_WARNING_THRESHOLD
      ? 'usage'
      : undefined;

  return {
    bannerVariant,
    isChatDisabled: isExhausted && !hasUnavailableState,
    percentage,
    popoverResetDate: formatTokenUsagePopoverResetDate(usage?.reset_at),
    resetDate: formatTokenUsageResetDate(usage?.reset_at),
    showControl:
      percentage !== undefined && percentage >= TOKEN_USAGE_CONTROL_THRESHOLD,
  };
};

export const isMoreRecentTokenUsage = (
  currentUsage: TokenUsage | undefined,
  nextUsage: TokenUsage
): boolean => {
  if (!currentUsage) {
    return true;
  }

  return Date.parse(nextUsage.as_of) > Date.parse(currentUsage.as_of);
};
